
import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { Session, User } from '@supabase/supabase-js';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { checkUserRole } from '@/utils/adminUtils';
import { logLoginActivity, logUserActivity, getUserIP } from '@/utils/activityLogger';
import { markMfaVerified, readMfaVerified } from '@/utils/mfaVerificationMarker';
import { clearOnboardingSession } from '@/utils/onboarding/sessionState';

type AuthContextType = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  adminLoading: boolean;
  mfaLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<{ success: boolean } | undefined>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  resetSession: () => void;
  mfaEnrolled: boolean;
  mfaVerified: boolean;
  assuranceLevel: number;
  checkMfaEnrollment: () => Promise<boolean>;
  enrollMfa: (code: string, factorType?: 'email' | 'sms', userEmail?: string, phoneNumber?: string) => Promise<{ success: boolean; error?: string }>;
  verifyMfa: (code: string, userEmail?: string, phoneNumber?: string, factorType?: 'email' | 'sms') => Promise<{ success: boolean; error?: string }>;
  isFirstTimeUser: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to clear all MFA-related session storage for a user
const clearMfaSessionStorage = (userEmail?: string) => {
  if (!userEmail) return;

  // Get all session storage keys and filter for MFA-related ones
  const keysToRemove = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.includes(`figabl_mfa_`) && key.includes(userEmail)) {
      keysToRemove.push(key);
    }
  }

  // Remove all MFA-related keys for this user
  keysToRemove.forEach(key => {
    sessionStorage.removeItem(key);
  });

  // Also clear general MFA completion flag
  sessionStorage.removeItem('figabl_mfa_completed');
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [adminLoading, setAdminLoading] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [mfaEnrolled, setMfaEnrolled] = useState(false);
  const [mfaVerified, setMfaVerified] = useState(false);
  const [assuranceLevel, setAssuranceLevel] = useState(0);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [adminCheckCache, setAdminCheckCache] = useState<{ email: string; isAdmin: boolean } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const queryClientRef = React.useRef(queryClient);
  queryClientRef.current = queryClient;

  // Read by the auth listener, which subscribes once. Holding these in refs
  // rather than the dep array keeps the subscription stable: re-subscribing
  // hands the new subscriber a fresh INITIAL_SESSION event, and before this the
  // effect re-subscribed on every navigation (its deps reached location.pathname
  // through performFallbackCleanup).
  const pathnameRef = React.useRef(location.pathname);
  const userEmailRef = React.useRef<string | undefined>(undefined);
  // Identity the current MFA state belongs to, so repeat events for the same
  // user can be told apart from an actual account change.
  const lastUserIdRef = React.useRef<string | null>(null);

  pathnameRef.current = location.pathname;
  userEmailRef.current = user?.email;

  useEffect(() => {
    if (user?.id) {
      Sentry.setUser({ id: user.id });
      return;
    }
    Sentry.setUser(null);
  }, [user?.id]);

  // Helper function to perform fallback cleanup
  const performFallbackCleanup = useCallback((userEmail?: string) => {
    // Clear MFA session storage before resetting state
    if (userEmail) {
      clearMfaSessionStorage(userEmail);
    }

    // Reset all auth state immediately
    setUser(null);
    setSession(null);
    setMfaEnrolled(false);
    setMfaVerified(false);
    setAssuranceLevel(0);
    setIsAdmin(false);
    setIsFirstTimeUser(false);
    setAdminCheckCache(null);
    setLoading(false);
    lastUserIdRef.current = null;

    // Only navigate to login if not on confirmation page
    if (pathnameRef.current !== '/confirmation') {
      navigate('/login');
      toast.success('Signed out successfully');
    } else {
      // Still show success message but don't redirect
      toast.success('Signed out successfully');
    }
  }, [navigate]);

  // Helper function to create timeout promise
  const createTimeoutPromise = useCallback((timeoutMs: number) => {
    return new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Timeout')), timeoutMs);
    });
  }, []);

  // Memoized admin status check with caching
  const checkAdminStatus = useCallback(async (email: string | undefined) => {
    if (!email) {
      return false;
    }

    // Check cache first
    if (adminCheckCache && adminCheckCache.email === email) {
      return adminCheckCache.isAdmin;
    }

    try {
      const adminStatus = await checkUserRole(email, 'admin');

      // Cache the result
      setAdminCheckCache({ email, isAdmin: adminStatus });
      return adminStatus;
    } catch (error) {
      console.error('[AuthProvider] Error checking admin status:', error);
      return false;
    }
  }, [adminCheckCache]);

  const checkMfaEnrollment = useCallback(async (): Promise<boolean> => {
    if (!user?.id && !user?.email) {
      return false;
    }

    setMfaLoading(true);

    try {
      let query = supabase
        .from('user_mfa_factors')
        .select('id')
        .eq('is_active', true)
        .neq('factor_type', 'device_session');

      // For authenticated users with user_id, check by user_id
      if (user?.id) {
        query = query.eq('user_id', user.id);
      } else if (user?.email) {
        // For magic link users without user_id, check by email in secret field
        query = query.eq('secret', user.email);
      } else {
        return false;
      }

      const { data, error } = await query.limit(1);

      if (error) {
        console.error('[AuthProvider] Error checking MFA enrollment:', error);
        throw error;
      }

      const enrolled = data && data.length > 0;
      setMfaEnrolled(enrolled);

      // Cache the enrollment status in session storage
      if (user?.email) {
        sessionStorage.setItem(`figabl_mfa_enrolled_${user.email}`, enrolled.toString());
      }

      return enrolled;
    } catch (error) {
      console.error('[AuthProvider] Error checking MFA enrollment:', error);
      setMfaEnrolled(false);
      return false;
    } finally {
      setMfaLoading(false);
    }
  }, [user?.id, user?.email]);

  // Separate effect for admin status checking - only runs when user email changes
  useEffect(() => {
    let mounted = true;

    const updateAdminStatus = async () => {
      if (!user?.email) {
        setIsAdmin(false);
        setAdminCheckCache(null);
        setAdminLoading(false);
        return;
      }

      setAdminLoading(true);

      const adminStatus = await checkAdminStatus(user.email);
      setIsAdmin(adminStatus);
      setAdminLoading(false);
    };

    updateAdminStatus();

    return () => {
      mounted = false;
    };
  }, [user?.email, checkAdminStatus]);

  // Separate effect for MFA enrollment checking with proper loading state
  useEffect(() => {
    let mounted = true;

    const updateMfaStatus = async () => {
      if (!user?.id && !user?.email) {
        if (mounted) {
          setMfaEnrolled(false);
          setMfaLoading(false);
          // Clear cached enrollment status if user is gone
          if (user?.email) {
            clearMfaSessionStorage(user.email);
          }
        }
        return;
      }

      // Check cached enrollment status first
      const cachedEnrollment = sessionStorage.getItem(`figabl_mfa_enrolled_${user?.email}`);
      if (cachedEnrollment === 'true' && mounted) {
        setMfaEnrolled(true);
        setMfaLoading(false);
        return; // Don't do database check if we have cached true result
      }

      // Always verify with database for new users or when cache is false/missing
      const actualEnrollment = await checkMfaEnrollment();

      if (mounted) {
        setMfaEnrolled(actualEnrollment);
      }
    };

    updateMfaStatus();

    return () => {
      mounted = false;
    };
  }, [user?.id, user?.email, checkMfaEnrollment]);

  // supabase-js re-emits SIGNED_IN / TOKEN_REFRESHED every time a hidden tab
  // becomes visible again (GoTrueClient#_onVisibilityChanged -> _recoverAndRefresh)
  // and hands every new subscriber an INITIAL_SESSION. None of those mean the
  // user re-authenticated, so MFA state may only be reset when the identity
  // actually changes - otherwise a tab switch silently drops a verified user
  // back to /verify-mfa.
  //
  // There is no `aal` to read back here: the session's assurance level is a
  // Supabase-native-MFA concept this app does not use, so verification is
  // tracked through the marker helpers above instead.
  const applyMfaStateForSession = useCallback((nextSession: Session | null) => {
    const nextUserId = nextSession?.user?.id ?? null;

    if (!nextUserId) {
      lastUserIdRef.current = null;
      setMfaVerified(false);
      setAssuranceLevel(0);
      return;
    }

    // Same user as the state we already hold - a refresh, a tab focus, or a
    // re-subscription. Leave verification alone.
    if (lastUserIdRef.current === nextUserId) return;

    lastUserIdRef.current = nextUserId;

    // First session seen in this tab, or a different account: trust only a
    // verification this tab actually recorded.
    const verified = readMfaVerified(nextSession?.user?.email, nextUserId);
    setMfaVerified(verified);
    setAssuranceLevel(verified ? 2 : 1);
  }, []);

  // Main auth state effect - modified to handle confirmation page
  useEffect(() => {
    let mounted = true;

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) {
          return;
        }

        // Handle session expiry or sign out - clear MFA storage
        if (event === 'SIGNED_OUT' || (event === 'TOKEN_REFRESHED' && !session)) {
          // Don't interrupt active magic link authentication on auth callback routes
          const isOnAuthCallbackRoute =
            pathnameRef.current === '/auth-callback' ||
            pathnameRef.current === '/reseller/auth-callback';
          if (!isOnAuthCallbackRoute) {
            const previousUserEmail = userEmailRef.current;
            if (previousUserEmail) {
              clearMfaSessionStorage(previousUserEmail);
            }
            queryClientRef.current.clear();
            performFallbackCleanup(previousUserEmail);
          }
          return;
        }

        // Only handle essential auth state updates here
        setSession(session);
        setUser(session?.user ?? null);

        applyMfaStateForSession(session);

        if (event === 'SIGNED_IN' && session?.user) {
          // Check if this is a first-time user
          if (session.user.email_confirmed_at) {
            const signedUpTime = new Date(session.user.email_confirmed_at).getTime();
            const now = new Date().getTime();
            // Consider first-time if confirmed less than 5 minutes ago
            if (now - signedUpTime < 5 * 60 * 1000) {
              setIsFirstTimeUser(true);
            }
          }

          setLoading(false);
        } else {
          setIsFirstTimeUser(false);
          setLoading(false);
        }
      }
    );

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session: initialSession } }) => {
      if (!mounted) return;

      setSession(initialSession);
      setUser(initialSession?.user ?? null);

      applyMfaStateForSession(initialSession);

      setLoading(false);
    });

    return () => {
      mounted = false;
      authListener.subscription.unsubscribe();
    };
  }, [performFallbackCleanup, applyMfaStateForSession]);

  const enrollMfa = async (
    code: string,
    factorType: 'email' | 'sms' = 'email',
    userEmail?: string,
    phoneNumber?: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const email = userEmail || user?.email;

      if (!email) {
        return { success: false, error: 'Email is required for MFA enrollment' };
      }

      // Verify the code first
      const { data, error } = await invokeFunction(supabase, 'verify-mfa-v3', {
        body: {
          token: code,
          factorType: factorType,
          userEmail: email,
          phoneNumber: phoneNumber,
          action: 'enroll',
          userId: user?.id
        }
      });

      if (error) {
        console.error('Error verifying MFA code:', error);
        return { success: false, error: 'Failed to verify code' };
      }

      if (!data.valid) {
        return { success: false, error: 'Invalid verification code' };
      }

      // Get user_id - if user is not authenticated (MFA reset), look up by email
      let userId = user?.id;

      if (!userId && userEmail) {
        try {
          const { data: lookupData, error: lookupError } = await invokeFunction(supabase, 'lookup-user-id', {
            body: { email: userEmail }
          });

          if (lookupError || !lookupData?.user_id) {
            console.error('Error looking up user ID:', lookupError);
            return { success: false, error: 'Failed to find user account' };
          }

          userId = lookupData.user_id;
        } catch (error) {
          console.error('Error in user lookup:', error);
          return { success: false, error: 'Failed to verify user account' };
        }
      }

      if (!userId) {
        return { success: false, error: 'Unable to identify user for MFA enrollment' };
      }

      // Store the MFA factor in the database
      const insertData: any = {
        user_id: userId,
        factor_type: factorType,
        factor_name: factorType === 'email' ? 'Email Verification' : 'SMS Verification',
        secret: email, // Store email as the secret for identification
        is_active: true,
        enrolled_at: new Date().toISOString()
      };

      // Add phone number for SMS factors
      if (factorType === 'sms' && phoneNumber) {
        insertData.phone_number = phoneNumber;
      }

      // Duplicate-guard: if a factor already exists for (user_id, secret, factor_type), update it
      const { data: existingFactor } = await supabase
        .from('user_mfa_factors')
        .select('id')
        .eq('user_id', userId)
        .eq('secret', email)
        .eq('factor_type', factorType)
        .maybeSingle();

      let writeError: any = null;
      if (existingFactor?.id) {
        const { error: updateError } = await supabase
          .from('user_mfa_factors')
          .update({
            is_active: true,
            enrolled_at: new Date().toISOString(),
            ...(factorType === 'sms' && phoneNumber ? { phone_number: phoneNumber } : {}),
          })
          .eq('id', existingFactor.id);
        writeError = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('user_mfa_factors')
          .insert(insertData);
        writeError = insertError;
      }

      if (writeError) {
        console.error('Error storing MFA factor:', writeError);
        return { success: false, error: 'Failed to store MFA enrollment' };
      }

      // Immediately update the MFA enrolled state and cache it
      setMfaEnrolled(true);
      sessionStorage.setItem(`figabl_mfa_enrolled_${email}`, 'true');

      // IMPORTANT: Set MFA as verified since the user just completed verification during setup
      setMfaVerified(true);
      setAssuranceLevel(2);
      markMfaVerified(email, userId);

      // Set a temporary flag to indicate MFA was just completed
      sessionStorage.setItem(`figabl_mfa_just_completed_${email}`, Date.now().toString());

      return { success: true };
    } catch (error: any) {
      console.error('Error enrolling MFA:', error);
      return { success: false, error: error.message || 'Failed to enroll MFA' };
    }
  };

  const verifyMfa = async (
    code: string,
    userEmail?: string,
    phoneNumber?: string,
    factorType: 'email' | 'sms' = 'email'
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const email = userEmail || user?.email;

      if (!email) {
        return { success: false, error: 'Email is required for MFA verification' };
      }

      const { data, error } = await invokeFunction(supabase, 'verify-mfa-v3', {
        body: {
          token: code,
          factorType: factorType,
          userEmail: email,
          phoneNumber: phoneNumber,
          action: 'verify'
        }
      });

      if (error) {
        console.error('Error verifying MFA:', error);
        return { success: false, error: 'Failed to verify code' };
      }

      if (!data.valid) {
        return { success: false, error: 'Invalid verification code' };
      }

      // Record the verification. Without this a successful verify left
      // mfaVerified false, so MfaProtectedRoute only let the user through on
      // the 5-minute figabl_mfa_just_completed_* window or the device cookie.
      setMfaVerified(true);
      setAssuranceLevel(2);
      markMfaVerified(email, user?.id);

      return { success: true };
    } catch (error: any) {
      console.error('Error verifying MFA:', error);
      return { success: false, error: error.message || 'Failed to verify MFA' };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Log the login activity after successful authentication
      if (data.session?.user) {
        try {
          await logLoginActivity(
            data.session.user.email || email,
            'password',
            data.session.user.id
          );
        } catch (logError) {
          // Don't block login if activity logging fails
          console.error('[AuthContext] Failed to log login activity:', logError);
        }
      }

      toast.success('Signed in successfully');
      navigate('/admin');
    } catch (error: any) {
      toast.error(error.message || 'An error occurred during sign in');
      console.error('Sign in error:', error);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) throw error;

      toast.success('Signed up successfully. Please check your email for verification.');
      return { success: true };
    } catch (error: any) {
      toast.error(error.message || 'An error occurred during sign up');
      console.error('Sign up error:', error);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    const startTime = Date.now();
    const currentUserEmail = user?.email;

    try {
      // Clear MFA enrollment cache before signing out
      if (currentUserEmail) {
        clearMfaSessionStorage(currentUserEmail);
      }

      // Drop any in-progress onboarding state too. Signing out is one of the exit paths that
      // used to leave an organisation's connection ids behind for the next form to pick up.
      clearOnboardingSession();

      // Create a timeout promise that rejects after 5 seconds
      const timeoutPromise = createTimeoutPromise(5000);

      // Race the supabase signOut against the timeout
      const signOutPromise = supabase.auth.signOut();

      try {
        const result = await Promise.race([signOutPromise, timeoutPromise]);

        if (result && typeof result === 'object' && 'error' in result && result.error) {
          console.error('Supabase sign out error:', result.error);
          throw result.error;
        }

        // The SIGNED_OUT event will be triggered by the auth state listener
        // which will clean up the local state and navigate

      } catch (timeoutError) {
        if (timeoutError instanceof Error && timeoutError.message === 'Timeout') {
          const elapsedTime = Date.now() - startTime;

          // Perform fallback cleanup since Supabase signOut didn't complete
          performFallbackCleanup(currentUserEmail);
          return;
        } else {
          throw timeoutError;
        }
      }

    } catch (error: any) {
      const elapsedTime = Date.now() - startTime;
      console.error(`Sign out error after ${elapsedTime}ms:`, error);

      // If sign out failed completely, perform fallback cleanup
      performFallbackCleanup(currentUserEmail);
      return;
    }
  };

  const resetSession = () => {
    toast.info('Form fields have been reset');
  };

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    session,
    user,
    loading,
    adminLoading,
    mfaLoading,
    signIn,
    signUp,
    signOut,
    isAdmin,
    resetSession,
    mfaEnrolled,
    mfaVerified,
    assuranceLevel,
    checkMfaEnrollment,
    enrollMfa,
    verifyMfa,
    isFirstTimeUser
  }), [
    session,
    user,
    loading,
    adminLoading,
    mfaLoading,
    isAdmin,
    mfaEnrolled,
    mfaVerified,
    assuranceLevel,
    isFirstTimeUser
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
